import { useAuth } from '@nest/common-core-frontend-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, Tabs, TabsContent, TabsList, TabsTrigger } from '@nest/uikit-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTerminalClient } from '../client-rpc-router';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';

type TabNames = 'receivedItems' | 'allItems';

export default function InboundDeliveryOrderProcessPage() {
	const client = useTerminalClient();
	const inboundDeliveryOrderId = useNumberParam('inboundDeliveryOrderId');
	const [clickedReceivedLineId, setClickedReceivedLineId] = useState<number | undefined>(undefined);
	const [activeTab, setActiveTab] = React.useState<TabNames>('receivedItems');
	const navigate = useNavigate();
	const auth = useAuth();
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: ['inbound-delivery-order', inboundDeliveryOrderId],
		queryFn: () =>
			client.inboundDeliveryOrders.getInboundDeliveryOrderById.execute({
				id: inboundDeliveryOrderId!,
				populate: ['lines', 'lines.stockItem', 'lines.stockItem.item', 'assignedTo']
			}),
		enabled: !!inboundDeliveryOrderId
	});

	const receivedLines = useQuery({
		queryKey: ['inbound-delivery-order-process-received-lines', inboundDeliveryOrderId],
		queryFn: () => client.inboundDeliveryOrderProcess.getReceivedLines.execute({ inboundDeliveryOrderId: inboundDeliveryOrderId! }),
		enabled: !!inboundDeliveryOrderId && activeTab === 'receivedItems'
	});

	const allLines = useQuery({
		queryKey: ['inbound-delivery-order-process-all-lines', inboundDeliveryOrderId],
		queryFn: () => client.inboundDeliveryOrderProcess.getAllLines.execute({ inboundDeliveryOrderId: inboundDeliveryOrderId! }),
		enabled: !!inboundDeliveryOrderId && activeTab === 'allItems'
	});

	const goToLineProcess = (lineId: number) => {
		navigate(`/inbound/delivery/orders/${inboundDeliveryOrderId}/process/${lineId}`);
	};

	const inboundDeliveryOrder = query.data;

	return (
		<TerminalScreen showBackButton topBar={<div>{inboundDeliveryOrder?.documentNumber}</div>}>
			<Tabs<TabNames> value={activeTab} onValueChange={value => setActiveTab(value)} className="h-full flex flex-col">
				<div className="flex flex-col h-full">
					<TabsContent value="receivedItems" className="h-full overflow-hidden">
						<div className="h-full overflow-auto">
							{receivedLines.data?.map(line => (
								<div key={line.id} onClick={() => setClickedReceivedLineId(line.id)} className="flex flex-row gap-2">
									<div>{line.stockItem?.item?.name}</div>
									<div>{line.quantity}</div>
								</div>
							))}
						</div>
					</TabsContent>
					<TabsContent value="allItems" className="h-full overflow-hidden">
						<div className="h-full overflow-auto">
							{allLines.data?.map(line => (
								<div key={line.id} onClick={() => goToLineProcess(line.id)} className="flex flex-row gap-2">
									<div>{line.stockItem?.item?.name}</div>
									<div>{line.quantity}</div>
								</div>
							))}
						</div>
					</TabsContent>
					<TabsList className="mt-auto">
						<TabsTrigger value="receivedItems">Přijaté</TabsTrigger>
						<TabsTrigger value="allItems">Přehled</TabsTrigger>
					</TabsList>
				</div>
			</Tabs>
			<ReceivedLineSheet inboundDeliveryOrderLineId={clickedReceivedLineId} />
		</TerminalScreen>
	);
}

const ReceivedLineSheet = React.memo(({ inboundDeliveryOrderLineId }: { inboundDeliveryOrderLineId: number | undefined }) => {
	const [open, setOpen] = React.useState(false);

	React.useEffect(() => {
		setOpen(inboundDeliveryOrderLineId !== undefined);
	}, [inboundDeliveryOrderLineId]);

	return (
		<Sheet open={open} onOpenChange={setOpen}>
			<SheetContent className="w-full" side="bottom">
				<SheetHeader>
					<SheetTitle>Are you absolutely sure?</SheetTitle>
					<SheetDescription>This action cannot be undone. This will permanently delete your account and remove your data from our servers.</SheetDescription>
				</SheetHeader>
			</SheetContent>
		</Sheet>
	);
});
